import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import { ArrowLeft, ArrowRight, X, TrendingUp, Play, Video, Youtube } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import "swiper/css";
import "swiper/css/navigation";

/**
 * NonYoutubeVideosSection
 * Displays project videos (self-hosted MP4s and real YouTube reels/shorts)
 * as a dark, auto-scrolling horizontal swiper.
 * Each card: video preview on hover (self-hosted) or YouTube thumbnail,
 * name + engagement at bottom, circular redirect button.
 * shape: { id, url, transfer_link, interaction_number }
 */

// Matches youtu.be/ID, youtube.com/watch?v=ID, youtube.com/shorts/ID,
// youtube.com/embed/ID — returns null for anything else (self-hosted files).
const getYouTubeId = (url) => {
    if (!url) return null;
    const match = String(url).match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
};

const VideoCard = ({ video, idx, onClick }) => {
    const videoRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [thumbError, setThumbError] = useState(false);

    const url = video?.url || video;
    const transferLink = video?.transfer_link;
    const interactionNum = video?.interaction_number;
    const youtubeId = getYouTubeId(url);

    // Try to load first frame (self-hosted videos only)
    useEffect(() => {
        if (youtubeId) return;
        const el = videoRef.current;
        if (!el) return;
        const onLoad = () => {
            el.currentTime = 0.1; // jump to first frame for thumbnail
            setLoaded(true);
        };
        el.addEventListener("loadeddata", onLoad);
        el.addEventListener("error", () => setError(true));
        return () => {
            el.removeEventListener("loadeddata", onLoad);
        };
    }, [youtubeId]);

    return (
        <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group select-none border border-white/10 shadow-lg shadow-black/20 ring-1 ring-transparent transition-all duration-300 hover:shadow-2xl hover:shadow-primary-900/30 hover:ring-primary-500/40 hover:-translate-y-1"
            style={{ aspectRatio: "9/16" }}
            onClick={onClick}
            onMouseEnter={() => {
                const v = videoRef.current;
                if (v && !error && !youtubeId) {
                    v.muted = true;
                    v.currentTime = 0;
                    v.play().catch(() => {});
                }
            }}
            onMouseLeave={() => {
                const v = videoRef.current;
                if (v && !youtubeId) {
                    v.pause();
                    v.currentTime = 0.1;
                }
            }}
        >
            {/* ── Background ── */}
            {youtubeId ? (
                <img
                    src={
                        thumbError
                            ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
                            : `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
                    }
                    alt={`فيديو ${idx + 1}`}
                    loading="lazy"
                    onError={() => setThumbError(true)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            ) : !error ? (
                <video
                    ref={videoRef}
                    src={url}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            ) : (
                /* Fallback when video cannot load */
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                    <Video className="w-12 h-12 text-white/20" />
                </div>
            )}

            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/15 transition-opacity group-hover:from-black/60" />

            {/* Platform badge (top corner) */}
            {youtubeId && (
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                    <Youtube className="w-4 h-4 text-white" fill="currentColor" />
                </div>
            )}

            {/* ── Play button (always visible, brightens on hover) ── */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/15 group-hover:bg-white/30 backdrop-blur-sm border border-white/25 flex items-center justify-center transition-all duration-300 group-hover:scale-110 shadow-xl">
                    <Play className="w-6 h-6 text-white fill-white ms-1" />
                </div>
            </div>

            {/* ── Bottom Info Row ── */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
                {/* Engagement */}
                <div className="flex items-center gap-1.5 mb-3">
                    <TrendingUp className="w-3 h-3 text-blue-400 flex-shrink-0" />
                    <span className="text-[11px] text-white/60">معدل التفاعل:</span>
                    <span className="text-[11px] font-bold text-blue-400 truncate">
                        {interactionNum || "عالي جداً"}
                    </span>
                </div>

                {/* Name + Redirect button */}
                <div className="flex items-end justify-between gap-2">
                    <p className="text-white font-semibold text-sm truncate leading-tight">
                        فيديو {idx + 1}
                    </p>

                    {transferLink && (
                        <a
                            href={transferLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0 w-9 h-9 rounded-full bg-white flex items-center justify-center text-gray-900 hover:bg-blue-500 hover:text-white transition-all duration-200 shadow-lg"
                            title="شاهد العمل"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────── */

const NonYoutubeVideosSection = ({ videos = [], heading = "فيديوهات المشروع" }) => {
    const swiperRef = useRef(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const selectedUrl = selectedVideo?.url || selectedVideo;
    const selectedYoutubeId = getYouTubeId(selectedUrl);
    const isShort = String(selectedUrl || "").includes("/shorts/");

    if (!videos || videos.length === 0) return null;

    return (
        <>
            {/* ─── Section ─── */}
            <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.08 }}
                transition={{ duration: 0.5 }}
                className="relative bg-white py-14 overflow-hidden"
            >
                <div className="wrapper relative z-10">
                    {/* Heading */}
                    <div className="text-center mb-8">
                        <span className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-600 text-xs font-bold px-4 py-1.5 rounded-full mb-3">
                            <Video className="w-3.5 h-3.5" />
                            محتوى مرئي
                        </span>
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                            {heading}
                        </h2>
                    </div>

                    {/* Swiper + Nav */}
                    <div className="relative">
                        {/* Prev (RTL: right side) */}
                        <button
                            onClick={() => swiperRef.current?.slidePrev()}
                            className="absolute -right-3 lg:-right-7 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white hover:bg-primary-600 hover:text-white border border-gray-200 text-gray-700 transition-all shadow-md hover:shadow-lg"
                        >
                            <ArrowRight className="w-5 h-5" />
                        </button>

                        {/* Next (RTL: left side) */}
                        <button
                            onClick={() => swiperRef.current?.slideNext()}
                            className="absolute -left-3 lg:-left-7 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white hover:bg-primary-600 hover:text-white border border-gray-200 text-gray-700 transition-all shadow-md hover:shadow-lg"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>

                        <Swiper
                            modules={[Navigation, Autoplay]}
                            onSwiper={(s) => (swiperRef.current = s)}
                            slidesPerView={1.5}
                            spaceBetween={12}
                            dir="rtl"
                            loop={videos.length > 5}
                            autoplay={{
                                delay: 2600,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true,
                            }}
                            speed={800}
                            breakpoints={{
                                640: { slidesPerView: 2.5, spaceBetween: 14 },
                                1024: { slidesPerView: 4, spaceBetween: 16 },
                                1280: { slidesPerView: 5, spaceBetween: 16 },
                            }}
                            className="px-2 py-2"
                        >
                            {videos.map((video, idx) => (
                                <SwiperSlide key={idx}>
                                    <VideoCard
                                        video={video}
                                        idx={idx}
                                        onClick={() => setSelectedVideo(video)}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </motion.section>

            {/* ─── Modal ─── */}
            <AnimatePresence>
                {selectedVideo && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedVideo(null)}
                    >
                        <motion.div
                            className={selectedYoutubeId && isShort ? "relative w-full max-w-sm" : "relative w-full max-w-3xl"}
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: "spring", damping: 25 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close */}
                            <button
                                onClick={() => setSelectedVideo(null)}
                                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                            >
                                <X size={28} />
                            </button>

                            {/* Player */}
                            <div
                                className={`rounded-2xl overflow-hidden bg-black ${
                                    selectedYoutubeId ? (isShort ? "aspect-[9/16]" : "aspect-video") : "aspect-video"
                                }`}
                            >
                                {selectedYoutubeId ? (
                                    <iframe
                                        key={selectedYoutubeId}
                                        src={`https://www.youtube.com/embed/${selectedYoutubeId}?autoplay=1&rel=0`}
                                        title={`فيديو ${selectedVideo?.name || ""}`}
                                        className="w-full h-full"
                                        allow="autoplay; encrypted-media; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : (
                                    <video
                                        key={selectedUrl}
                                        src={selectedUrl}
                                        controls
                                        controlsList="nodownload"
                                        onContextMenu={(e) => e.preventDefault()}
                                        autoPlay
                                        className="w-full h-full object-contain"
                                    />
                                )}
                            </div>

                            {/* Footer */}
                            <div className="mt-4 flex items-center justify-between gap-4 bg-white/5 backdrop-blur-md rounded-2xl px-5 py-4">
                                <div className="flex items-center gap-2">
                                    <TrendingUp size={15} className="text-blue-400" />
                                    <span className="text-white/60 text-sm">معدل التفاعل:</span>
                                    <span className="text-blue-400 font-bold text-sm">
                                        {selectedVideo?.interaction_number || "عالي جداً"}
                                    </span>
                                </div>

                                {selectedVideo?.transfer_link && (
                                    <a
                                        href={selectedVideo.transfer_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-500/20"
                                    >
                                        شاهد العمل
                                        <ArrowLeft size={15} />
                                    </a>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default NonYoutubeVideosSection;
